import { useState } from 'react'
import { ChevronDown, Warehouse } from 'lucide-react'
import { Card } from '@/components/ui/card'
import {
  DC_TOLERANCE,
  dcDelta,
  dcLabel,
  dcUnits,
  type DcRowFilter,
  type DcTotals,
} from './dc-model'

/** The "selected wave / all waves" switch of a DC summary. */
export function DcScopeToggle({
  label,
  waveOption,
  all,
  onChange,
}: {
  label: string
  /** The selected wave's option ("This wave"). */
  waveOption: string
  all: boolean
  onChange: (all: boolean) => void
}) {
  return (
    <div className="flex rounded-md bg-secondary p-0.5" aria-label={label}>
      {[false, true].map(value => (
        <button
          key={String(value)}
          type="button"
          aria-pressed={all === value}
          className={`cursor-pointer rounded px-2.5 py-1 text-[11px] ${all === value ? 'bg-card font-medium shadow-sm' : 'text-muted-foreground'}`}
          onClick={() => onChange(value)}
        >
          {value ? 'All waves' : waveOption}
        </button>
      ))}
    </div>
  )
}

interface Props {
  totals: DcTotals
  allWeeksTotals: DcTotals
  lineCount: number
  waveLabel: string
  splitIssueCount: number
  filter: DcRowFilter
  onFilter: (filter: DcRowFilter) => void
}

/** Selected-wave totals; the issue counts double as row filters. */
export function DcWaveSummary({
  totals,
  allWeeksTotals,
  lineCount,
  waveLabel,
  splitIssueCount,
  filter,
  onFilter,
}: Props) {
  const [showDcTotals, setShowDcTotals] = useState(false)
  const [allWaves, setAllWaves] = useState(false)
  const stats: {
    id: string
    label: string
    value: string
    hint: string
    warning?: boolean
    filter?: DcRowFilter
  }[] = [
    {
      id: 'dc-planned',
      label: 'Planned wave units',
      value: dcUnits(totals.waveQty),
      hint: `${lineCount} SKU lines · ${waveLabel}`,
    },
    {
      id: 'dc-adjusted',
      label: 'Adjusted DC units',
      value: dcUnits(totals.adjusted),
      hint: `${dcDelta(totals.balance)} units net vs. wave`,
    },
    {
      id: 'dc-split-issues',
      label: 'Split issues',
      value: String(splitIssueCount),
      hint: splitIssueCount
        ? 'Below / above 100% · View lines'
        : 'All SKU lines allocated to 100%',
      warning: splitIssueCount > 0,
      filter: 'split',
    },
    {
      id: 'dc-excess',
      label: 'Excess units',
      value: dcDelta(totals.excess),
      hint: `${dcDelta(totals.excessPercent)}% of wave · ${totals.excessCount} lines`,
      warning: totals.excessCount > 0,
      filter: 'over',
    },
  ]
  const dcTotals = allWaves ? allWeeksTotals : totals

  return (
    <>
      <Card
        role="region"
        className="gap-0 overflow-hidden py-0 shadow-none"
        aria-label="Selected wave totals"
      >
        <div className="grid grid-cols-2 sm:grid-cols-4">
          {stats.map(stat => {
            const Stat = stat.filter ? 'button' : 'div'
            const active = !!stat.filter && filter === stat.filter
            return (
              <Stat
                key={stat.id}
                {...(stat.filter
                  ? {
                      type: 'button' as const,
                      'aria-pressed': active,
                      onClick: () => onFilter(active ? 'all' : stat.filter!),
                    }
                  : {})}
                className={`min-w-0 border-r border-b border-border/60 px-3 py-2.5 text-left even:border-r-0 sm:border-b-0 sm:px-4 sm:even:border-r sm:last:border-r-0 ${stat.filter ? 'cursor-pointer hover:bg-secondary/40 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary' : ''} ${active ? 'bg-secondary/60 ring-1 ring-inset ring-primary' : ''}`}
              >
                <span className="block text-[11px] font-medium text-muted-foreground">
                  {stat.label}
                </span>
                <span
                  data-testid={stat.id}
                  className={`mt-0.5 block text-xl font-semibold tabular-nums ${stat.warning ? 'text-amber-800' : ''}`}
                >
                  {stat.value}
                </span>
                <span className="mt-0.5 block text-[11px] text-muted-foreground">
                  {stat.hint}
                </span>
              </Stat>
            )
          })}
        </div>
        <section
          role="region"
          className="border-t-0 sm:border-t"
          aria-label="Totals by distribution center"
        >
          <div
            className={`flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 ${showDcTotals ? 'border-b' : ''}`}
          >
            <h2 className="min-w-0 text-xs font-semibold">
              <button
                type="button"
                aria-expanded={showDcTotals}
                aria-controls={showDcTotals ? 'dc-totals-panel' : undefined}
                onClick={() => setShowDcTotals(!showDcTotals)}
                className="flex cursor-pointer items-center gap-2 rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                <Warehouse size={14} />
                Totals by DC{' '}
                <span className="font-normal text-muted-foreground">
                  · all SKU lines · excludes Parent
                </span>
              </button>
            </h2>
            <div className="flex items-center gap-2">
              {showDcTotals && (
                <DcScopeToggle
                  label="DC totals scope"
                  waveOption="Selected wave"
                  all={allWaves}
                  onChange={setAllWaves}
                />
              )}
              <button
                type="button"
                aria-label={`${showDcTotals ? 'Collapse' : 'Expand'} Totals by DC`}
                aria-expanded={showDcTotals}
                aria-controls={showDcTotals ? 'dc-totals-panel' : undefined}
                onClick={() => setShowDcTotals(!showDcTotals)}
                className="flex size-7 cursor-pointer items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                <ChevronDown
                  size={14}
                  className={`transition-transform ${showDcTotals ? 'rotate-180' : ''}`}
                />
              </button>
            </div>
          </div>
          {showDcTotals && (
            <div
              className="dc-totals-grid"
              id="dc-totals-panel"
              data-testid="dc-totals"
            >
              {dcTotals.centers.map(dc => (
                <div key={dc.id} className="min-w-0 px-4 py-3">
                  <p
                    className="text-[11px] font-medium text-muted-foreground"
                    title={dcLabel(dc)}
                  >
                    {dc.code}
                    {dc.name && dc.name !== dc.code && (
                      <span className="block truncate font-normal">
                        {dc.name}
                      </span>
                    )}
                  </p>
                  <p className="mt-1 text-lg font-semibold tabular-nums">
                    {dcUnits(dc.adjusted)}
                  </p>
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    {dcUnits(dc.raw)} theoretical
                  </p>
                  <p
                    className={`mt-0.5 text-[10px] tabular-nums ${Math.abs(dc.adjusted - dc.raw) > DC_TOLERANCE ? 'text-amber-800' : 'text-muted-foreground'}`}
                  >
                    {dcDelta(dc.adjusted - dc.raw)} adjusted
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>
      </Card>
    </>
  )
}
